import React, { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { KeyboardArrowDown } from "@mui/icons-material";
import ArrowPopper from "components/app/ArrowPopper";
import { Divider, Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from "moment";
import { useBppById, useBppDetailByKey } from "../hooks";
import { motion } from "framer-motion";
import { Skeleton } from "@mui/material";
import { BppDetail } from "types/portfolio";
import { classHOC } from "components/app/ClassWrapper";

const bppDetailConfig = [
  { label: "Beta Program ID", field: "id" },
  { label: "Beta Program Name", field: "name" },
  { label: "Start Date", field: "startDate" },
  { label: "End Date", field: "endDate" },
];

const useStyles = makeStyles(() => ({
  listContainer: {
    maxHeight: "52vh",
    overflowY: "auto",
    scrollbarWidth: "thin", //FF
    "&::-webkit-scrollbar": {
      width: 4,
    },
    paddingRight: 8,
  },
  cardContainer: {
    borderRadius: 6,
    border: "1px solid #DEDEDE",
    marginTop: 8,
  },
  itemContainer: {
    display: "flex",
    width: "100%",
    marginTop: 8,
    padding: "0 12px",
    "&:last-of-type": {
      marginBottom: 8,
    },
  },
  itemLabel: {
    fontSize: 12,
    color: "#898989",
    lineHeight: "16px",
    width: 100,
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  itemValue: {
    fontSize: 12,
    lineHeight: "16px",
    color: "#333333",
    display: "flex",
    alignItems: "center",
    margin: "0 60px 0 24px",
  },
}));

const BetaProgramRenderer: FC<
  Partial<ICellRendererParams> & {
    onlyShowCount?: boolean;
    isAssembed?: boolean; // for detail-contact-filter need combine bppDetail&bpp
  }
> = ({ data, value, node, onlyShowCount = false, isAssembed = false }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);

  const popRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: bppList } = useBppById(anchorEl ? data?.accountId : undefined);
  const { data: bppDetail } = useBppDetailByKey(
    onlyShowCount && anchorEl ? data?.contactKey : undefined
  );

  useEffect(() => {
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const closePop = (e: Event) => {
    if (
      !popRef.current?.contains(e.target as Node) &&
      popRef.current !== e.target &&
      !rootRef.current?.contains(e.target as Node)
    ) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  if (node?.isRowPinned()) {
    return <></>;
  }
  const val = isAssembed
    ? value?.split("|")?.[0] && value?.split("|")?.[0] > 0
      ? value?.split("|")?.[0]
      : ""
    : value;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: val ? "pointer" : "default",
          width: "fit-content",
        }}
        ref={rootRef}
        onClick={val ? handleClick : undefined}
      >
        {val ? (
          !onlyShowCount ? (
            <span style={{ color: "#00AB50" }}>
              Yes <span style={{ color: "#898989" }}>({val})</span>
            </span>
          ) : (
            <span style={{ color: "#898989" }}>{val}</span>
          )
        ) : (
          <span style={{ color: "#CF2030" }}>No</span>
        )}
        {val ? <KeyboardArrowDown style={{ fontSize: "1rem" }} /> : null}
      </div>
      {anchorEl ? (
        onlyShowCount ? (
          <ArrowPopper
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            placement="right"
            disablePortal={false}
          >
            <div
              ref={popRef}
              className={classes.listContainer}
            >
              {/* <AnimatePresence initial={false}> */}
              <p
                style={{
                  textTransform: "uppercase",
                  fontSize: 11,
                  color: "#121212",
                  fontWeight: 400,
                }}
              >
                beta program participation
                <span style={{ color: "#535759", marginLeft: 5 }}>
                  (
                  {bppDetail !== undefined ? (
                    (bppDetail?.length ?? 0)
                  ) : (
                    <Skeleton
                      width={10}
                      height={8}
                      variant="rectangular"
                      style={{ display: "inline-block" }}
                    />
                  )}
                  )
                </span>
              </p>
              {bppDetail !== undefined ? (
                bppDetail?.map((detail) => (
                  <div
                    key={detail.id}
                    className={classes.cardContainer}
                    style={{
                      background: detail?.endDate ? "#DEDEDE" : "#fff",
                    }}
                  >
                    <motion.div
                      exit={{ opacity: 0 }}
                      key={detail?.id}
                    >
                      {bppDetailConfig.map((bpp) => (
                        <div
                          className={classes.itemContainer}
                          key={bpp.field}
                        >
                          <div className={classes.itemLabel}>{bpp.label}</div>
                          <div
                            className={classes.itemValue}
                            style={{
                              color: detail?.endDate ? "#898989" : "#333",
                            }}
                          >
                            <span
                              style={{
                                maxWidth: 300,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                // paddingRight: 20,
                              }}
                            >
                              {bpp.label.includes("Date")
                                ? detail[bpp?.field as keyof BppDetail]
                                  ? moment(detail[bpp?.field as keyof BppDetail])
                                      .utc()
                                      .format("MM/DD/YYYY")
                                  : "--"
                                : detail[bpp?.field as keyof BppDetail]}
                            </span>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  </div>
                ))
              ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {Array.from({ length: 4 }).map((x, i) => (
                    <Skeleton
                      key={`${x}${i}`}
                      width={300}
                      height={32}
                      variant="rectangular"
                      style={{ margin: "6px 12px" }}
                    />
                  ))}
                </div>
              )}

              {/* </AnimatePresence> */}
            </div>
          </ArrowPopper>
        ) : (
          <ArrowPopper
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            placement="right"
            disablePortal={false}
          >
            <div
              ref={popRef}
              className={classes.listContainer}
            >
              <a
                target="_blank"
                rel="noreferrer"
                href="https://gobeta.webex.com/welcome/"
                style={{ display: "flex", justifyContent: "end " }}
              >
                Beta Program Page
              </a>
              <Grid
                container={true}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Grid
                  item={true}
                  style={{ fontWeight: 700, color: "#333", marginRight: 40 }}
                >
                  Beta Program
                </Grid>
                <Grid
                  item={true}
                  style={{ fontWeight: 700, color: "#333" }}
                >
                  Users Participating
                </Grid>
              </Grid>
              <Divider style={{ background: "#666", margin: "3px 0" }} />
              <Grid
                container={true}
                direction="column"
                xs={12}
              >
                {bppList !== undefined ? (
                  bppList?.details?.map((item, i) => (
                    <Grid
                      item={true}
                      container={true}
                      key={item.program}
                      justifyContent="space-between"
                      style={{ marginTop: i === 0 ? 0 : 10 }}
                      alignItems="center"
                    >
                      <Grid
                        item={true}
                        style={{
                          color: "#666",
                          fontSize: 12,
                          fontWeight: 400,
                          lineHeight: "16px",
                        }}
                      >
                        {item.program}
                      </Grid>
                      <Grid
                        item={true}
                        style={{
                          color: "#333",
                          fontSize: 12,
                          lineHeight: "16px",
                          fontWeight: 500,
                          marginLeft: 10,
                        }}
                      >
                        {item.participation}
                      </Grid>
                    </Grid>
                  ))
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {Array.from({ length: 4 }).map((x, i) => (
                      <Skeleton
                        key={`${x}${i}`}
                        width={232}
                        height={32}
                        variant="rectangular"
                        style={{ margin: "4px" }}
                      />
                    ))}
                  </div>
                )}
              </Grid>
            </div>
          </ArrowPopper>
        )
      ) : null}
    </>
  );
};

export default classHOC(BetaProgramRenderer);
